import {Button} from '@kidslabs/mario-ui-nextjs-react-core';
import ErrorMessage from '@/_application/core/components/ui/error-message';
import ProductLoader from '@/_application/core/components/ui/loaders/product-loader';
import NotFound from '@/_application/core/components/ui/not-found';
import {getImageAssetUrl} from '@/_application/core/utils/common';
import {ProductCard} from '@/_application/modules/products/components/cards/product-card';
import {PRODUCTS_PER_PAGE} from '@/framework/client/variables';
import {ProductListItemsQuery} from '@/framework/gql/products.graphql';
import rangeMap from '@/lib/range-map';
import cn from 'classnames';
import {useTranslation} from 'next-i18next';
import SearchEmpty from '@/_application/pages/search/search-empty';
import {ProductInterface} from '@/__generated__/__types__';
import {Product} from '@/_application/modules/products/model/product';
import ObserveChildren from '@/_application/core/components/commons/obverse-children';

const babySadImage = getImageAssetUrl('images/baby-sad.png');

interface Props {
    limit?: number;
    sortedBy?: string;
    orderBy?: string;
    shopId?: string;
    products: ProductListItemsQuery['products'];
    isLoading?: boolean;
    error?: any;
    loadMore?: any;
    isLoadingMore?: boolean;
    hasMore?: boolean;
    className?: string;
    isSearch?: boolean;
    onProductsLoaded?: () => void;
}

export function ProductGrid({
    className,
    products,
    isLoading,
    error,
    loadMore,
    isLoadingMore,
    hasMore,
    limit = PRODUCTS_PER_PAGE,
    isSearch = false,
    onProductsLoaded,
}: Readonly<Props>) {
    const {t} = useTranslation('common');
    if (error) return <ErrorMessage message={error.message} />;

    if (!isLoading && !products?.items?.length) {
        return (
            <>
                {isSearch ? (
                    <SearchEmpty />
                ) : (
                    <div className="min-h-full w-full px-4 pb-8 pt-6 lg:px-8 lg:pb-8">
                        <NotFound text="text-not-found" className="mx-auto w-7/12" imageUrl={babySadImage} />
                    </div>
                )}
            </>
        );
    }

    const handleProductsLoaded = () => {
        onProductsLoaded?.();
    };

    return (
        <div className={cn('w-full', className)}>
            <div className="mt-5 grid gap-5 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {isLoading &&
                    !products?.items?.length &&
                    rangeMap(limit, i => <ProductLoader key={i} uniqueKey={`product-${i}`} />)}
            </div>

            <ObserveChildren onChildrenChange={handleProductsLoaded}>
                <div className="mt-5 grid gap-5 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                    {products?.items?.map((product, index) =>
                        // Currently only support 2 types
                        product && ['SimpleProduct', 'ConfigurableProduct'].includes(product.__typename ?? '') ? (
                            <ProductCard isFirstProductInList={index === 0} product={product as ProductInterface} key={`${(product as Product)?.id}`} />
                        ) : (
                            <></>
                        ),
                    )}
                </div>
            </ObserveChildren>
            {hasMore && (
                <div className="mt-8 flex justify-center lg:mt-12">
                    <Button
                        loading={isLoadingMore}
                        onClick={loadMore}
                        aria-label="Tải thêm sản phẩm"
                        className="h-11 text-sm font-semibold md:text-base">
                        {t('text-load-more')}
                    </Button>
                </div>
            )}
        </div>
    );
}
